import React from 'react';
import {observer} from 'mobx-react';
import {ProductCustomizationState} from './render.def';
import {fitUVLinePt} from './uv-fit';
// IMPORTANT: MUST NOT import from s.ts (product store) - this renderer code is used in multiple apps for image rendering

interface Props {
    pcs: ProductCustomizationState;
}

// Relative luminance test for a #rgb / #rrggbb colour. Light text (the UV-on-mat
// white / gold / silver) keeps the dark halo that lifts it off the mat; dark text
// (the INFORMS awards' navy on frosted glass) prints clean without one. Anything
// unparseable counts as light, the historic behaviour.
export const isLightUVColor = (hex: string): boolean => {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());

    if (!m) return true;

    let h = m[1];

    if (h.length === 3) {
        h = h
            .split('')
            .map(c => c + c)
            .join('');
    }

    const channel = (i: number): number => {
        const c = parseInt(h.slice(i, i + 2), 16) / 255;

        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4) > 0.5;
};

// Old-style (CustomizationMethodID < 2) UV print overlay. The >= 2 path paints UV
// lines inside the customization canvas (renderUVPrint reads pcs.uv_lines into
// css.uv_line_styles); old/none frames are static photos, so the lines are painted
// over them here. Same coordinate model as the engraved-plate overlay: each line's
// x/y are inches from the photo center (+X right, +Y down), scaled by the physical
// inches the photo spans into percentages of the shrink-wrapped photo box, so the
// overlay holds at any displayed size (responsive, zoom, headless imagegen). Each
// line box is size-contained with its aspect ratio, and the font renders at pt/72
// inches of the box height via cqh - mirroring the plate preview's container-query
// scaling.
export const UVOverlay = observer(({pcs}: Props) => {
    if (!pcs.is_uv_print || !pcs.uv_print_active) {
        return null;
    }

    // The photo's physical span. A UV template can state it (options.photo_width /
    // photo_height: a product photo that is not a frame, e.g. the INFORMS glass awards,
    // whose JF_* stay 0); otherwise the frame size plus the moulding ext width
    // (JF_Width/JF_Height + MouldingExtWidth), as the plate overlay uses.
    const extW = pcs.MouldingExtWidth > 0 ? pcs.MouldingExtWidth : 0;
    const frameW = pcs.uv_photo_w > 0 ? pcs.uv_photo_w : pcs.JF_Width + extW;
    const frameH = pcs.uv_photo_h > 0 ? pcs.uv_photo_h : pcs.JF_Height + extW;

    if (!(frameW > 0) || !(frameH > 0)) {
        return null;
    }

    // A line paints its text or, on the product page of a required template, its
    // placeholder (the proof's "Name" / "Date") until the shopper types.
    const lines = (pcs.uv_lines || []).filter(ln => ln && ((ln.text || '').trim() !== '' || (ln.placeholder || '').trim() !== '') && ln.max_width > 0 && ln.max_height > 0 && ln.pt > 0);

    if (lines.length === 0) {
        return null;
    }

    return (
        <>
            {lines.map((ln, idx) => {
                const isPlaceholder = (ln.text || '').trim() === '';
                const text = isPlaceholder ? ln.placeholder || '' : ln.text;
                const weight = ln.weight && ln.weight > 0 ? ln.weight : 400;
                const color = ln.color || '#fff';
                // Auto-shrink (never enlarge) the template pt so long text stays
                // inside the line's max_width box.
                const fittedPt = fitUVLinePt(text, ln.fontFamily, ln.pt, ln.max_width, weight);

                return (
                    <div
                        key={`uv_overlay_line_${idx}`}
                        id={`uv_overlay_line_${idx + 1}`}
                        className={isPlaceholder ? 'uv-overlay-placeholder' : undefined}
                        style={{
                            position: 'absolute',
                            left: `${50 + (ln.x / frameW) * 100}%`,
                            top: `${50 + (ln.y / frameH) * 100}%`,
                            transform: 'translate(-50%, -50%)',
                            width: `${(ln.max_width / frameW) * 100}%`,
                            aspectRatio: `${ln.max_width} / ${ln.max_height}`,
                            containerType: 'size',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color,
                            fontFamily: ln.fontFamily,
                            fontWeight: weight,
                            textAlign: 'center',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 5,
                            opacity: isPlaceholder ? 0.45 : 1,
                            textShadow: isLightUVColor(color) ? '0 0 2px rgba(0, 0, 0, 0.55), 0 1px 2px rgba(0, 0, 0, 0.35)' : 'none',
                        }}
                    >
                        {/* cqh must sit on a CHILD of the size-container: container-query units
                        resolve against the nearest ANCESTOR container, so on the container
                        div itself they'd fall back to viewport units. */}
                        <span style={{fontSize: `${(fittedPt / (ln.max_height * 72)) * 100}cqh`}}>{text}</span>
                    </div>
                );
            })}
        </>
    );
});
